/**
 * eReg Sidebar Navigation Component
 *
 * Phase 1 (friction audit §8 item 10): sections are always visible. The study
 * section shows an inline picker when no study is anchored (instead of hiding
 * its items), the duplicate "Documents" entry is gone, Signatures is always
 * shown (the queue is user-global), and Upload/Intake are told apart with
 * "Single document" / "Bulk inbox" sublabels. Items link to the canonical
 * study/site URLs; without a scope they hit the legacy paths, which redirect
 * through the last-opened study and then the dashboard.
 */

import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderTree,
  FileText,
  Upload,
  PenTool,
  Settings,
  X,
  ClipboardList,
  Globe,
  Inbox,
  ScrollText,
} from 'lucide-react';
import { useEreg } from '../../contexts/EregContext';
import { getStudyName, sortStudiesForDisplay } from '../../types';
import { fieldCls } from '../../../ui';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  onClick?: () => void;
}

function NavItem({ to, icon, label, sublabel, onClick }: NavItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex min-h-[44px] items-center gap-3 rounded-lg px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 lg:min-h-0 lg:py-1.5 ${
          isActive
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
        }`
      }
    >
      {icon}
      <span className="min-w-0">
        <span className="block truncate">{label}</span>
        {sublabel && (
          <span className="block text-[10px] leading-tight text-gray-400 dark:text-gray-500">{sublabel}</span>
        )}
      </span>
    </NavLink>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="pt-4 pb-2">
      <p className="truncate px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
        {children}
      </p>
    </div>
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isAdmin, currentProtocol, siteMode, protocols, protocolsLoading } = useEreg();
  const navigate = useNavigate();

  const handleNavClick = () => {
    // Close sidebar on mobile when navigation happens
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  // Item targets: study-scoped when a study is anchored, the site zone in
  // site-documents mode, otherwise the legacy paths (LegacySectionRoute
  // rehydrates the last-opened study or falls back to the dashboard).
  const studyBase = currentProtocol ? `/ereg/studies/${currentProtocol.id}` : null;
  const foldersTo = studyBase ? `${studyBase}/folders` : siteMode ? '/ereg/site/folders' : '/ereg/folders';
  const uploadTo = studyBase ? `${studyBase}/upload` : siteMode ? '/ereg/site/upload' : '/ereg/upload';
  const signaturesTo = studyBase ? `${studyBase}/signatures` : '/ereg/signatures';
  const delegationTo = studyBase ? `${studyBase}/delegation` : '/ereg/delegation';

  // Group the inline picker by site when studies span more than one.
  const siteNames = new Map<string, string>();
  for (const p of protocols) {
    if (!siteNames.has(p.site_id)) siteNames.set(p.site_id, p.site?.name || 'Other site');
  }
  const multiSite = siteNames.size > 1;

  const handlePickStudy = (protocolId: string) => {
    if (!protocolId) return;
    navigate(`/ereg/studies/${protocolId}/folders`);
    handleNavClick();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700
          transform transition-transform duration-200 ease-in-out
          lg:transform-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Mobile header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Menu</span>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {/* Dashboard */}
            <NavItem
              to="/ereg/dashboard"
              icon={<LayoutDashboard size={18} />}
              label="Dashboard"
              onClick={handleNavClick}
            />

            {/* Signature queue — user-global, always reachable (N-5) */}
            <NavItem
              to={signaturesTo}
              icon={<PenTool size={18} />}
              label="Signatures"
              onClick={handleNavClick}
            />

            {/* Study / site-documents section — always visible */}
            <GroupLabel>
              {siteMode ? 'Site Documents' : currentProtocol ? getStudyName(currentProtocol) : 'Study'}
            </GroupLabel>

            {!currentProtocol && !siteMode && (
              <div className="pb-1">
                <label htmlFor="ereg-sidebar-study-picker" className="sr-only">
                  Select a study
                </label>
                <select
                  id="ereg-sidebar-study-picker"
                  value=""
                  onChange={(e) => handlePickStudy(e.target.value)}
                  disabled={protocolsLoading || protocols.length === 0}
                  className={`${fieldCls} w-full`}
                >
                  <option value="">
                    {protocolsLoading
                      ? 'Loading studies…'
                      : protocols.length === 0
                        ? 'No studies available'
                        : 'Select a study…'}
                  </option>
                  {multiSite
                    ? Array.from(siteNames.entries()).map(([siteId, name]) => (
                        <optgroup key={siteId} label={name}>
                          {sortStudiesForDisplay(protocols.filter((p) => p.site_id === siteId)).map((p) => (
                            <option key={p.id} value={p.id}>
                              {getStudyName(p)}
                            </option>
                          ))}
                        </optgroup>
                      ))
                    : sortStudiesForDisplay(protocols).map((p) => (
                        <option key={p.id} value={p.id}>
                          {getStudyName(p)}
                        </option>
                      ))}
                </select>
              </div>
            )}

            <NavItem
              to={foldersTo}
              icon={<FolderTree size={18} />}
              label="Folders"
              onClick={handleNavClick}
            />

            <NavItem
              to={uploadTo}
              icon={<Upload size={18} />}
              label="Upload"
              sublabel="Single document"
              onClick={handleNavClick}
            />

            {/* Delegation is per-study; the site-documents zone has none */}
            {!siteMode && (
              <NavItem
                to={delegationTo}
                icon={<ClipboardList size={18} />}
                label="Delegation Log"
                onClick={handleNavClick}
              />
            )}

            {/* Network-wide reference documents — visible to every eReg user */}
            <GroupLabel>Network</GroupLabel>

            <NavItem
              to="/ereg/network"
              icon={<Globe size={18} />}
              label="Network Documents"
              onClick={handleNavClick}
            />

            {/* Admin section */}
            {isAdmin && (
              <>
                <GroupLabel>Admin</GroupLabel>

                <NavItem
                  to="/ereg/intake"
                  icon={<Inbox size={18} />}
                  label="Intake"
                  sublabel="Bulk inbox"
                  onClick={handleNavClick}
                />

                <NavItem
                  to="/ereg/protocols"
                  icon={<FileText size={18} />}
                  label="Manage Studies"
                  onClick={handleNavClick}
                />

                <NavItem
                  to="/ereg/audit"
                  icon={<ScrollText size={18} />}
                  label="Audit Trail"
                  onClick={handleNavClick}
                />
              </>
            )}

            {/* Settings */}
            <GroupLabel>Settings</GroupLabel>

            <NavItem
              to="/ereg/settings"
              icon={<Settings size={18} />}
              label="Preferences"
              onClick={handleNavClick}
            />
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              Electronic Regulatory Binder
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
